import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/components/AuthContext";
import { Loader2, Lock, Mail, Eye, EyeOff } from "lucide-react";

const LOGO = process.env.PUBLIC_URL + "/logo-central.png";

function formatErr(detail) {
  if (!detail) return "Ocurrió un error. Intenta de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").join(" ");
  return String(detail);
}

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [show, setShow] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail) || "Correo o contraseña incorrectos");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0F172A] px-4 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(#E2333F 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#E2333F]/20 blur-3xl" />

      <div className="relative w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <img src={LOGO} alt="ZAE" className="h-16 w-16 rounded-lg object-contain bg-black mb-3" />
          <div className="font-display font-black text-2xl tracking-[0.18em] text-white">
            ZAE <span className="font-light">EJECUTIVO</span>
          </div>
          <div className="text-sm text-white/50 mt-1">Grupo Tomza · Acceso ejecutivo</div>
        </div>

        <form onSubmit={submit} className="bg-white rounded-md shadow-2xl p-7" data-testid="login-form">
          <h1 className="font-display font-bold text-xl text-slate-900 mb-1">Iniciar sesión</h1>
          <p className="text-sm text-slate-400 mb-5">Ingresa tus credenciales para continuar</p>

          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Correo</label>
          <div className="relative mb-4">
            <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
              placeholder="correo@zae2.mx"
              className="w-full h-11 pl-9 pr-3 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-[#E2333F] focus:border-[#E2333F]"
            />
          </div>

          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Contraseña</label>
          <div className="relative mb-5">
            <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type={show ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
              placeholder="••••••••"
              className="w-full h-11 pl-9 pr-10 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-[#E2333F] focus:border-[#E2333F]"
            />
            <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          {error && (
            <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-sm px-3 py-2" data-testid="login-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full h-11 rounded-sm bg-[#E2333F] text-white font-display font-bold text-sm hover:bg-[#c92a36] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? "Ingresando…" : "Entrar"}
          </button>
        </form>
        <p className="text-center text-xs text-white/40 mt-5">© Grupo Tomza · ZAE Ejecutivo</p>
      </div>
    </div>
  );
}
